import type { HrMetricId } from '../services/hrImportData';
import { isDepartmentHiddenInUi, looksLikeDepartmentId } from './departmentUi';
import { HR_KPI_DEFINITIONS } from './hrKpiDefinitions';

/**
 * User-visible ward names for HR tables.
 * Keys stay canonical ids. Values are Thai names with the id removed.
 * NSO and CSSD return null so callers can omit the row.
 */
const HR_UNIT_DISPLAY_NAME: Record<string, string> = {
  ERD: 'หน่วยอุบัติเหตุและฉุกเฉิน',
  OPD_GENP: 'คลินิกเวชปฏิบัติ',
  BLO_BNK: 'หน่วยรับบริจาคโลหิต',
  OPD_SxOrthoEENT: 'คลินิกศัลยกรรม ศัลยกรรมกระดูกและข้อ และคลินิกตา หู คอ จมูก',
  OPD_OBGYN: 'คลินิกสูติ-นรีเวชกรรมและกุมารเวชกรรม',
  OPD_MED: 'คลินิกอายุรกรรม',
  OPD_CHEMO: 'คลินิกเคมีบำบัด',
  HD: 'หน่วยไตเทียม',
  ECC: 'ศูนย์ส่งเสริมฟื้นฟูสุขภาพผู้สูงอายุ',
  '18C': 'สามัญตา หู คอ จมูก',
  '26B': 'พิเศษทั่วไป',
  OR: 'ห้องผ่าตัด',
  G10: 'หน่วยส่องกล้อง',
  DR: 'ตึกคลอด',
  '18B': 'สามัญนรีเวชกรรมและศัลยกรรมหญิง',
  '18A': 'สามัญสูติกรรม',
  '23A': 'พิเศษสูติกรรม',
  NICU: 'วิกฤตทารกแรกเกิด',
  '18D': 'สามัญกุมารเวชกรรม',
  '21A': 'เคมีบำบัด',
  '21D': 'เคมีบำบัด',
  '19B': 'สามัญศัลยกรรมกระดูกและข้อชาย',
  '19C': 'สามัญศัลยกรรม ศัลยกรรมกระดูกและข้อหญิง',
  '19D': 'สามัญศัลยกรรม ศัลยกรรมกระดูกและข้อหญิง',
  '25A': 'พิเศษศัลยกรรมกระดูกและข้อ',
  '20B': 'ศัลยกรรมอุบัติเหตุชาย',
  '20C': 'ศัลยกรรมชาย',
  '25B': 'พิเศษศัลยกรรม',
  '22A': 'สามัญอายุรกรรมหญิง 1',
  STROKE: 'หน่วยโรคหลอดเลือดสมอง',
  '22D': 'สามัญอายุรกรรมหญิง 2',
  SW: 'ตึกสว่างวัฒนา',
  '22B': 'สามัญอายุรกรรมชาย 1',
  '22C': 'สามัญอายุรกรรมชาย 2',
  '19A': 'สามัญอายุรกรรมชาย 3',
  '24A': 'พิเศษอายุรกรรม',
  '24B': 'พิเศษกุมารเวชกรรม',
  MICU: 'วิกฤตอายุรกรรมและวิกฤตกุมาร',
  CCU: 'วิกฤตโรคหัวใจและหลอดเลือด',
  SICU: 'วิกฤตศัลยกรรม',
  BURN: 'หน่วยไฟไหม้',
  PICU: 'วิกฤตกุมาร',
  'NSO (ผู้บริหาร)': 'ผู้บริหารฝ่ายการพยาบาล',
  'CSSD (ซักฟอก และโรงนึ่ง)': 'ซักฟอกและโรงนึ่ง',
  'SICU (PICU)': 'วิกฤตกุมาร',
  ผู้บริหาร: 'ผู้บริหาร',
  หอพัก: 'หอพัก',
};

const FREE_TEXT_REPLACEMENTS: ReadonlyArray<readonly [RegExp, string]> = [
  [/SICU/g, 'วิกฤตศัลยกรรม'],
  [/MICU/g, 'วิกฤตอายุรกรรม'],
  [/PICU/g, 'วิกฤตกุมาร'],
  [/CSSD/g, 'หน่วยจ่ายกลาง'],
  [/NSO/g, 'ฝ่ายการพยาบาล'],
  [/EENT/g, 'ตา หู คอ จมูก'],
];

/** Null means the unit must not appear (NSO, CSSD). */
export function hrUnitDisplayName(raw: string | null | undefined): string | null {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return 'ไม่ระบุชื่อหน่วยงาน';
  if (isDepartmentHiddenInUi(trimmed)) return null;
  const mapped = HR_UNIT_DISPLAY_NAME[trimmed];
  if (mapped) return mapped;
  if (looksLikeDepartmentId(trimmed)) return 'ไม่ทราบชื่อหน่วยงาน';
  return hrScrubVisibleText(trimmed);
}

const ENGLISH_METRIC_LABEL = /happinometer|job\s*sat|turn[\s-]*over|^[A-Za-z0-9 ()%.-]+$/i;

/** Prefer a Thai label already stored on the row. Fall back to the indicator title when the stored label is English. */
export function hrMetricDisplayLabel(metricId: HrMetricId, metricLabelTh: string | null | undefined): string {
  const raw = String(metricLabelTh ?? '').trim();
  if (raw && !ENGLISH_METRIC_LABEL.test(raw)) return hrScrubVisibleText(raw);
  return HR_KPI_DEFINITIONS[metricId]?.titleTh || hrScrubVisibleText(raw);
}

/** Replace leftover English unit codes inside a sentence. Does not hide a row. */
export function hrScrubVisibleText(raw: string | null | undefined): string {
  let text = String(raw ?? '').trim();
  if (!text) return '';
  for (const [pattern, replacement] of FREE_TEXT_REPLACEMENTS) {
    text = text.replace(pattern, replacement);
  }
  return text.replace(/\s{2,}/g, ' ').trim();
}
